import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersApi, arApi, customerActivityApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Upload, Search, Loader2, Send, Download, ChevronRight, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowUpDown, Link2 as Link2Icon, Users as UsersIcon, IndianRupee, TrendingDown, FileWarning, Sparkles, ShieldCheck, Wallet, Clock, MessageSquare, Star, PhoneCall } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import Header from '../components/layout/Header';
import ChainOutreachSection from '../components/ChainOutreachSection';

const STATUS = {
  pending: { label: 'Pending', bg: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'In Progress', bg: 'bg-amber-50 text-amber-700' },
  complete: { label: 'Submitted', bg: 'bg-blue-50 text-blue-700' },
  approved: { label: 'Approved', bg: 'bg-emerald-50 text-emerald-700' },
  changes_requested: { label: 'Changes Required', bg: 'bg-red-50 text-red-700' },
};

const SKIP_VALUE = '__skip__';

// ₹ formatter — short for KPIs (12.4L / 2.1Cr), full otherwise
const _fmtINRShort = (n) => {
  if (!n || isNaN(n)) return '₹0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}\u20B9${(abs / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
  if (abs >= 1e5) return `${sign}\u20B9${(abs / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
  if (abs >= 1e3) return `${sign}\u20B9${(abs / 1e3).toFixed(0)}k`;
  return `${sign}\u20B9${Math.round(abs)}`;
};
const _fmtINR = (n) => {
  if (n == null || isNaN(n)) return '₹0';
  const sign = n < 0 ? '-' : '';
  const num = Math.round(Math.abs(n));
  let s = String(num);
  if (s.length <= 3) return `${sign}\u20B9${s}`;
  const last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  const groups = [];
  while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
  if (rest) groups.unshift(rest);
  return `${sign}\u20B9${groups.join(',')},${last3}`;
};

const Customers = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('customers'); // 'customers' | 'outreach'
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  // Smart "view" — quick segments based on AR data
  const [view, setView] = useState('all'); // all | overdue | open_invoices | needs_kyc | new
  const [designationFilter, setDesignationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ company_name: '', mobile: '', email: '', contact_person_name: '', contact_designation: 'operations' });
  const [inviting, setInviting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  // Mapping dialog state
  const [mappingOpen, setMappingOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null); // { columns, canonical_fields, sample_rows, total_rows }
  const [columnMap, setColumnMap] = useState({}); // { normalized_col: canonical_field | SKIP_VALUE }
  // AR receivables rollup per customer (keyed by id or by name::<company_name> for legacy rows)
  const [arSummary, setArSummary] = useState({});
  // Communication activity summary per customer (last_contact_at, open_followups, stale_days)
  const [activitySummary, setActivitySummary] = useState({});
  const fileRef = useRef(null);

  const fetch = async () => {
    try { const r = await customersApi.getAll(); setCustomers(r.data); }
    catch {} finally { setLoading(false); }
  };
  const fetchArSummary = async () => {
    try { const r = await arApi.customersReceivablesSummary(); setArSummary(r.data?.summary || {}); }
    catch { /* silent — AR view is optional */ }
  };
  const fetchActivitySummary = async () => {
    try { const r = await customerActivityApi.summary(); setActivitySummary(r.data || {}); }
    catch { /* silent */ }
  };
  useEffect(() => { fetch(); fetchArSummary(); fetchActivitySummary(); }, []);

  const toggleChampion = async (c, e) => {
    e.stopPropagation();
    const next = !c.is_champion;
    // Optimistic update
    setCustomers(prev => prev.map(x => x.id === c.id ? { ...x, is_champion: next } : x));
    try {
      await customersApi.setChampion(c.id, next);
      toast.success(next ? `${c.company_name} marked as Champion ⭐` : `Removed from Champions`);
    } catch (err) {
      // Revert
      setCustomers(prev => prev.map(x => x.id === c.id ? { ...x, is_champion: !next } : x));
      toast.error(err?.response?.data?.detail || 'Failed to update Champion status');
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.company_name || !inviteForm.mobile || !inviteForm.contact_person_name) {
      toast.error('Company name, contact person, and WhatsApp are required');
      return;
    }
    setInviting(true);
    try {
      await customersApi.invite(inviteForm);
      toast.success('Invite sent!');
      setInviteOpen(false);
      setInviteForm({ company_name: '', mobile: '', email: '', contact_person_name: '', contact_designation: 'operations' });
      fetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setInviting(false); }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const r = await customersApi.importPreview(file);
      const initialMap = {};
      r.data.columns.forEach(c => {
        initialMap[c.normalized] = c.auto_mapped_to || SKIP_VALUE;
      });
      setPreview(r.data);
      setColumnMap(initialMap);
      setPendingFile(file);
      setMappingOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to read file');
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingFile) return;
    // Build server payload: only mapped (non-SKIP) entries
    const serverMap = {};
    Object.entries(columnMap).forEach(([col, field]) => {
      if (field && field !== SKIP_VALUE) serverMap[col] = field;
    });
    // Validate: must have at least company_name OR mobile mapped
    const mappedFields = new Set(Object.values(serverMap));
    if (!mappedFields.has('company_name') && !mappedFields.has('mobile')) {
      toast.error('Map at least Company Name or Mobile to continue');
      return;
    }
    setImporting(true);
    try {
      const r = await customersApi.importFile(pendingFile, serverMap);
      setImportResult(r.data);
      toast.success(`${r.data.created} new · ${r.data.updated} updated · ${r.data.skipped} skipped`);
      setMappingOpen(false);
      setPendingFile(null);
      setPreview(null);
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const counts = { all: customers.length };
  Object.keys(STATUS).forEach(k => { counts[k] = customers.filter(c => c.kyc_status === k).length; });

  // Helper: get the AR rollup for a customer (supports legacy name-keyed rows)
  const getAR = (c) => arSummary[c.id] || arSummary[`name::${c.company_name}`] || null;
  const getActivity = (c) => activitySummary[c.id] || null;

  // Portfolio KPIs across the whole book
  const kpi = customers.reduce((acc, c) => {
    const ar = getAR(c);
    if (ar) {
      acc.outstanding += ar.outstanding || 0;
      acc.overdue += ar.overdue || 0;
      if (ar.open_count > 0) acc.with_open += 1;
      if (ar.overdue > 0) acc.with_overdue += 1;
    }
    return acc;
  }, { outstanding: 0, overdue: 0, with_open: 0, with_overdue: 0 });

  // (stale helper inlined below to match the 'overdue' filter pattern)

  const viewCounts = {
    all: customers.length,
    champions: customers.filter(c => c.is_champion).length,
    overdue: customers.filter(c => (getAR(c)?.overdue || 0) > 0).length,
    open_invoices: customers.filter(c => (getAR(c)?.open_count || 0) > 0).length,
    needs_kyc: customers.filter(c => c.kyc_status !== 'approved').length,
    new: customers.filter(c => {
      if (!c.created_at) return false;
      const days = (Date.now() - new Date(c.created_at).getTime()) / 86400000;
      return days <= 14;
    }).length,
    stale: customers.filter(c => (getAR(c)?.open_count > 0) && (!getActivity(c)?.last_contact_at || (getActivity(c)?.stale_days || 0) > 30)).length,
  };

  const filtered = customers
    .filter(c => {
      // View segment
      const ar = getAR(c);
      if (view === 'champions' && !c.is_champion) return false;
      if (view === 'overdue' && !(ar?.overdue > 0)) return false;
      if (view === 'open_invoices' && !(ar?.open_count > 0)) return false;
      if (view === 'needs_kyc' && c.kyc_status === 'approved') return false;
      if (view === 'new') {
        if (!c.created_at) return false;
        const days = (Date.now() - new Date(c.created_at).getTime()) / 86400000;
        if (days > 14) return false;
      }
      if (view === 'stale' && !((ar?.open_count > 0) && (!getActivity(c)?.last_contact_at || (getActivity(c)?.stale_days || 0) > 30))) return false;
      if (filter !== 'all' && c.kyc_status !== filter) return false;
      if (designationFilter !== 'all' && (c.contact_designation || 'operations') !== designationFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      // Search also across additional contact names / labels / phones (nested contacts on KYC)
      const contactHit = (c.additional_phones || []).some(p =>
        (p?.name || '').toLowerCase().includes(q)
        || (p?.label || '').toLowerCase().includes(q)
        || (p?.phone || '').includes(q)
        || (p?.email || '').toLowerCase().includes(q)
      );
      return (c.company_name || '').toLowerCase().includes(q)
        || (c.mobile || '').includes(q)
        || (c.email || '').toLowerCase().includes(q)
        || (c.contact_person_name || '').toLowerCase().includes(q)
        || contactHit;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc': return (a.company_name || '').localeCompare(b.company_name || '');
        case 'name_desc': return (b.company_name || '').localeCompare(a.company_name || '');
        case 'oldest': return (a.created_at || '').localeCompare(b.created_at || '');
        case 'kyc_most': return (b.kyc_step || 0) - (a.kyc_step || 0);
        case 'kyc_least': return (a.kyc_step || 0) - (b.kyc_step || 0);
        case 'updated': return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '');
        case 'outstanding_desc': return (getAR(b)?.outstanding || 0) - (getAR(a)?.outstanding || 0);
        case 'overdue_desc': return (getAR(b)?.overdue || 0) - (getAR(a)?.overdue || 0);
        case 'recent':
        default: return (b.created_at || '').localeCompare(a.created_at || '');
      }
    });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="customers-page">
      <Header title="Customers" />

      <div className="max-w-screen-2xl mx-auto px-3 md:px-8 py-4 md:py-6 space-y-5 md:space-y-6">
        {/* Title + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <UsersIcon className="h-5 w-5 text-amber-700 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold tracking-tight">All Customers</h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-0.5">{customers.length} total · {counts.approved || 0} KYC approved</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="h-10 sm:h-9">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Import</span>
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open(customersApi.exportCSV(), '_blank')} data-testid="export-btn" className="h-10 sm:h-9">
              <Download className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button size="sm" onClick={() => setInviteOpen(true)} className="h-10 sm:h-9 bg-amber-600 hover:bg-amber-700 text-white flex-1 sm:flex-initial" data-testid="invite-customer-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Invite<span className="hidden sm:inline"> Customer</span>
            </Button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileSelected} />
          </div>
        </div>

        {importResult && (
          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 text-sm flex items-center justify-between">
            <span>Import complete — <strong>{importResult.created}</strong> new, <strong>{importResult.updated}</strong> updated</span>
            <button onClick={() => setImportResult(null)} className="text-sm underline text-muted-foreground">Dismiss</button>
          </div>
        )}

        {/* Section Tabs — Customers list vs Chain Outreach */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-5">
          <TabsList className="grid grid-cols-2 w-full sm:w-96 h-auto" data-testid="customers-section-tabs">
            <TabsTrigger value="customers" className="py-2 text-xs sm:text-sm" data-testid="tab-customers-list">
              <UsersIcon className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" /> Customers
            </TabsTrigger>
            <TabsTrigger value="outreach" className="py-2 text-xs sm:text-sm" data-testid="tab-chain-outreach">
              <PhoneCall className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" /> Chain Outreach
            </TabsTrigger>
          </TabsList>

          <TabsContent value="outreach" className="mt-0" data-testid="tab-content-outreach">
            <ChainOutreachSection allCustomers={customers} />
          </TabsContent>

          <TabsContent value="customers" className="mt-0 space-y-5 md:space-y-6" data-testid="tab-content-customers">

        {/* KPI strip — at-a-glance portfolio health */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 md:gap-3" data-testid="customers-kpi-strip">
          <KpiTile
            icon={UsersIcon}
            label="Total customers"
            value={customers.length}
            accent="text-amber-600"
            bg="bg-amber-50 dark:bg-amber-500/10"
            testid="kpi-total"
          />
          <KpiTile
            icon={ShieldCheck}
            label="KYC approved"
            value={`${counts.approved || 0}/${customers.length || 0}`}
            sub={customers.length > 0 ? `${Math.round(((counts.approved || 0) / customers.length) * 100)}% complete` : ''}
            accent="text-emerald-600"
            bg="bg-emerald-50 dark:bg-emerald-500/10"
            testid="kpi-approved"
          />
          <KpiTile
            icon={Wallet}
            label="Outstanding"
            value={_fmtINRShort(kpi.outstanding)}
            sub={`${kpi.with_open} customer${kpi.with_open === 1 ? '' : 's'}`}
            accent="text-violet-600"
            bg="bg-violet-50 dark:bg-violet-500/10"
            testid="kpi-outstanding"
          />
          <KpiTile
            icon={TrendingDown}
            label="Overdue"
            value={_fmtINRShort(kpi.overdue)}
            sub={kpi.with_overdue > 0 ? `${kpi.with_overdue} customer${kpi.with_overdue === 1 ? '' : 's'} late` : 'All current 🎉'}
            accent={kpi.overdue > 0 ? 'text-rose-600' : 'text-emerald-600'}
            bg={kpi.overdue > 0 ? 'bg-rose-50 dark:bg-rose-500/10' : 'bg-emerald-50 dark:bg-emerald-500/10'}
            testid="kpi-overdue"
          />
          <KpiTile
            icon={FileWarning}
            label="Pending KYC"
            value={viewCounts.needs_kyc}
            sub={viewCounts.needs_kyc > 0 ? 'Action needed' : 'All set'}
            accent={viewCounts.needs_kyc > 0 ? 'text-amber-600' : 'text-emerald-600'}
            bg="bg-slate-50 dark:bg-neutral-800/50"
            testid="kpi-pending-kyc"
          />
        </div>

        {/* Quick views — segmented entry points */}
        <div className="flex gap-2 items-center overflow-x-auto -mx-1 px-1 scrollbar-none" data-testid="customer-quick-views">
          {[
            { key: 'all', label: 'All customers', icon: UsersIcon, tone: 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900' },
            { key: 'champions', label: 'Champions', icon: Star, tone: 'bg-amber-500 text-white' },
            { key: 'overdue', label: 'Has overdue', icon: TrendingDown, tone: 'bg-rose-600 text-white' },
            { key: 'open_invoices', label: 'Open invoices', icon: Wallet, tone: 'bg-violet-600 text-white' },
            { key: 'needs_kyc', label: 'Needs KYC', icon: FileWarning, tone: 'bg-amber-600 text-white' },
            { key: 'new', label: 'New (14d)', icon: Sparkles, tone: 'bg-emerald-600 text-white' },
            { key: 'stale', label: 'Stale (>30d)', icon: Clock, tone: 'bg-orange-600 text-white' },
          ].map(v => {
            const Active = view === v.key;
            const Icon = v.icon;
            return (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={cn(
                  "flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 border",
                  Active
                    ? `${v.tone} border-transparent shadow-sm`
                    : "bg-white dark:bg-neutral-900 text-slate-700 dark:text-slate-200 border-slate-200/70 dark:border-neutral-800 hover:border-slate-300"
                )}
                data-testid={`view-${v.key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {v.label}
                <span className={cn("ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full", Active ? "bg-white/20" : "bg-slate-100 dark:bg-neutral-800 text-muted-foreground")}>
                  {viewCounts[v.key] || 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search + Filters card */}
        <div className="p-3 md:p-4 rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/70 dark:border-neutral-800 shadow-sm space-y-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, contact, phone or email..." className="pl-12 h-12 text-base rounded-xl" data-testid="customer-search" />
          </div>
          <div className="flex gap-2 items-center overflow-x-auto -mx-1 px-1 pb-1 scrollbar-none">
            {[
              { key: 'all', label: 'All' },
              { key: 'approved', label: 'Approved' },
              { key: 'complete', label: 'Submitted' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'changes_requested', label: 'Changes Required' },
              { key: 'pending', label: 'Pending' },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={cn("px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0",
                  filter === f.key ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-muted/60 text-muted-foreground hover:bg-muted"
                )} data-testid={`filter-${f.key}`}>
                {f.label} <span className="ml-1 opacity-60">{counts[f.key] || 0}</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap items-stretch sm:items-center justify-between">
            <div className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0">
              <span className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-semibold flex-shrink-0">Designation</span>
              <Select value={designationFilter} onValueChange={setDesignationFilter}>
                <SelectTrigger className="h-10 sm:h-9 text-sm flex-1 sm:w-40 sm:flex-initial" data-testid="designation-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="operations">Operations</SelectItem>
                  <SelectItem value="accounts">Accounts</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0">
              <span className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1 flex-shrink-0"><ArrowUpDown className="h-3 w-3" /> Sort</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-10 sm:h-9 text-sm flex-1 sm:w-52 sm:flex-initial" data-testid="sort-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Recently added</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="updated">Recently updated</SelectItem>
                  <SelectItem value="outstanding_desc">Outstanding (high → low)</SelectItem>
                  <SelectItem value="overdue_desc">Overdue (high → low)</SelectItem>
                  <SelectItem value="name_asc">Name (A → Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z → A)</SelectItem>
                  <SelectItem value="kyc_most">KYC: most complete</SelectItem>
                  <SelectItem value="kyc_least">KYC: least complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* List card */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white dark:bg-neutral-900 border border-dashed border-slate-200 dark:border-neutral-800">
            <p className="text-lg text-muted-foreground">{search || filter !== 'all' ? 'No customers match' : 'No customers yet'}</p>
            <p className="text-sm text-muted-foreground mt-1">Invite your first customer to get started</p>
          </div>
        ) : (
          <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/70 dark:border-neutral-800 shadow-sm overflow-hidden">
            <div className="divide-y divide-slate-100 dark:divide-neutral-800">
              {filtered.map(c => {
                const s = STATUS[c.kyc_status] || STATUS.pending;
                const ar = getAR(c);
                const city = c.kyc?.step_2?.billing_city || c.billing_city || '';
                const act = getActivity(c);
                const stale = act?.stale_days != null && act.stale_days > 30;
                const neverContacted = !act?.last_contact_at;
                const fmtLast = (sd) => sd == null ? null : (sd === 0 ? 'today' : sd === 1 ? 'yesterday' : sd < 30 ? `${sd}d ago` : sd < 365 ? `${Math.floor(sd / 30)}mo ago` : `${Math.floor(sd / 365)}y ago`);
                return (
                  <div key={c.id} onClick={() => navigate(`/customers/${c.id}`)}
                    className="flex items-center gap-3 sm:gap-4 py-4 sm:py-4 px-3 md:px-5 cursor-pointer hover:bg-amber-50/40 dark:hover:bg-amber-500/5 transition-colors"
                    data-testid={`customer-${c.id}`}>
                    {/* Champion star toggle */}
                    <button
                      type="button"
                      onClick={(e) => toggleChampion(c, e)}
                      className={cn(
                        "flex-shrink-0 p-1 rounded-md transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/10",
                        c.is_champion ? "text-amber-500" : "text-slate-300 dark:text-neutral-700"
                      )}
                      title={c.is_champion ? "Champion customer — click to remove" : "Mark as Champion"}
                      data-testid={`champion-toggle-${c.id}`}
                    >
                      <Star className={cn("h-4 w-4 sm:h-5 sm:w-5", c.is_champion && "fill-current")} />
                    </button>
                    {/* Avatar */}
                    <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-base sm:text-lg font-bold text-amber-700 dark:text-amber-400 flex-shrink-0">
                      {(c.company_name || '?')[0].toUpperCase()}
                    </div>
                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm sm:text-base font-semibold truncate">{c.company_name || 'Unnamed'}</p>
                        {(c.linked_customer_ids || []).length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300 flex-shrink-0 flex items-center gap-0.5" title={`${c.linked_customer_ids.length} linked`}>
                            <Link2Icon className="h-2.5 w-2.5" /> {c.linked_customer_ids.length}
                          </span>
                        )}
                        {ar && ar.overdue > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300 flex-shrink-0 font-semibold" title={`${ar.oldest_days_overdue}d overdue`}>
                            {ar.oldest_days_overdue}d late
                          </span>
                        )}
                        {(act?.open_followups || 0) > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 flex-shrink-0 font-semibold flex items-center gap-0.5" title={`${act.open_followups} pending follow-up`}>
                            <Clock className="h-2.5 w-2.5" /> {act.open_followups}
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate flex items-center gap-2">
                        <span className="truncate">
                          {c.mobile}{city ? ` · ${city}` : ''}{c.email ? ` · ${c.email}` : ''}
                        </span>
                        {neverContacted ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-neutral-800 flex items-center gap-0.5 flex-shrink-0" data-testid={`last-contact-${c.id}`}>
                            <MessageSquare className="h-2.5 w-2.5" /> never contacted
                          </span>
                        ) : (
                          <span
                            className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0',
                              stale ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                            )}
                            data-testid={`last-contact-${c.id}`}
                          >
                            <MessageSquare className="h-2.5 w-2.5" /> {fmtLast(act.stale_days)}
                          </span>
                        )}
                      </p>
                    </div>
                    {/* AR snapshot (hidden on mobile when no data) */}
                    {ar && (ar.outstanding > 0 || ar.open_count > 0) && (
                      <div className="hidden sm:flex flex-col items-end flex-shrink-0 min-w-[100px]" data-testid={`customer-ar-${c.id}`}>
                        <span className={cn("text-sm font-bold tabular-nums", ar.overdue > 0 ? "text-rose-600 dark:text-rose-400" : "text-slate-700 dark:text-slate-200")}>
                          {_fmtINR(ar.outstanding)}
                        </span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                          {ar.open_count} invoice{ar.open_count === 1 ? '' : 's'}
                        </span>
                      </div>
                    )}
                    {/* KYC status */}
                    <span className={cn("px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold whitespace-nowrap flex-shrink-0", s.bg)}>{s.label}</span>
                    {/* KYC step — hidden on mobile to save space */}
                    <span className="hidden md:inline text-sm text-muted-foreground tabular-nums w-12 text-right">{c.kyc_step || 0}/5</span>
                    <ChevronRight className="h-5 w-5 text-muted-foreground/30 flex-shrink-0" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>


      {/* Column Mapping Dialog */}
      <Dialog open={mappingOpen} onOpenChange={(o) => { if (!o) { setMappingOpen(false); setPendingFile(null); setPreview(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-amber-600" /> Review column mapping
            </DialogTitle>
            <DialogDescription>
              {preview && (
                <>
                  <span className="font-medium">{preview.filename}</span> · {preview.total_rows} rows ·
                  <span className="ml-1.5 text-emerald-700">{Object.values(columnMap).filter(v => v && v !== SKIP_VALUE).length} mapped</span> ·
                  <span className="ml-1.5 text-muted-foreground">{Object.values(columnMap).filter(v => !v || v === SKIP_VALUE).length} skipped</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="flex-1 overflow-y-auto -mx-6 px-6 py-3 space-y-2">
              {preview.columns.map((col) => {
                const value = columnMap[col.normalized] || SKIP_VALUE;
                const isMapped = value && value !== SKIP_VALUE;
                const wasAutoDetected = !!col.auto_mapped_to;
                const sampleValue = preview.sample_rows[0]?.[col.raw];
                return (
                  <div
                    key={col.normalized}
                    className={cn(
                      "grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center p-3 rounded-lg border transition-colors",
                      isMapped ? "border-emerald-200 bg-emerald-50/40 dark:bg-emerald-500/5" : "border-dashed border-amber-200 bg-amber-50/40 dark:bg-amber-500/5"
                    )}
                    data-testid={`mapping-row-${col.normalized}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        {isMapped
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          : <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />}
                        <p className="font-semibold text-sm truncate">{col.raw}</p>
                        {wasAutoDetected && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 flex-shrink-0">auto</span>
                        )}
                      </div>
                      {sampleValue && (
                        <p className="text-xs text-muted-foreground truncate ml-6">e.g. <span className="font-mono">{sampleValue}</span></p>
                      )}
                    </div>
                    <ChevronRight className="hidden sm:block h-4 w-4 text-muted-foreground/40" />
                    <Select
                      value={value}
                      onValueChange={(v) => setColumnMap(prev => ({ ...prev, [col.normalized]: v }))}
                    >
                      <SelectTrigger className="h-10" data-testid={`mapping-select-${col.normalized}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SKIP_VALUE}>
                          <span className="text-muted-foreground">— Skip this column —</span>
                        </SelectItem>
                        {/* Group fields by section */}
                        {['Company', 'Address', 'Documents', 'Banking', 'Contacts'].map(sec => (
                          <React.Fragment key={sec}>
                            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{sec}</div>
                            {preview.canonical_fields.filter(f => f.section === sec).map(f => (
                              <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2 border-t pt-4">
            <p className="text-xs text-muted-foreground sm:mr-auto">
              Tip: at least <strong>Company Name</strong> or <strong>Mobile</strong> must be mapped.
            </p>
            <Button variant="outline" onClick={() => { setMappingOpen(false); setPendingFile(null); setPreview(null); }}>Cancel</Button>
            <Button
              onClick={handleConfirmImport}
              disabled={importing}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-import-btn"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
              Import {preview?.total_rows || 0} rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Invite Customer</DialogTitle>
            <DialogDescription>A magic link will be sent to the contact person's WhatsApp. They'll log in using this number, so it becomes their primary login contact.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-1.5"><Label>Company Name *</Label><Input value={inviteForm.company_name} onChange={e => setInviteForm(p => ({ ...p, company_name: e.target.value }))} className="h-11" data-testid="invite-company" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5 sm:col-span-2"><Label>Contact Person *</Label><Input value={inviteForm.contact_person_name} onChange={e => setInviteForm(p => ({ ...p, contact_person_name: e.target.value }))} placeholder="Full name" className="h-11" data-testid="invite-contact" /></div>
              <div className="space-y-1.5">
                <Label>Designation *</Label>
                <Select value={inviteForm.contact_designation} onValueChange={v => setInviteForm(p => ({ ...p, contact_designation: v }))}>
                  <SelectTrigger className="h-11" data-testid="invite-designation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="accounts">Accounts</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>WhatsApp Number *</Label><Input value={inviteForm.mobile} onChange={e => setInviteForm(p => ({ ...p, mobile: e.target.value }))} placeholder="919876543210" className="h-11 font-mono" data-testid="invite-mobile" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} className="h-11" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="send-invite-btn">
              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// At-a-glance KPI tile used on the Customers landing
const KpiTile = ({ icon: Icon, label, value, sub, accent = '', bg = 'bg-slate-50', testid }) => (
  <div
    className={cn("p-3 md:p-4 rounded-xl border border-slate-200/70 dark:border-neutral-800 shadow-sm", bg)}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
      {Icon && <Icon className={cn("h-3.5 w-3.5", accent)} />} {label}
    </div>
    <p className={cn("mt-1 text-lg md:text-2xl font-bold tabular-nums leading-tight", accent)}>{value}</p>
    {sub && <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
  </div>
);

export default Customers;
